/** `generateOperation`: codename, theatre order and the whole node map from one seed (03 §6, 04 §2). */

import { Pcg32 } from '../../sim/rng';
import type { Biome } from '../../sim/types';
import { archetype, BIOMES, isOriginal } from '../content/archetypes';
import { hazardRules } from '../content/career';
import codenames from '../content/codenames.json';
import { MAP } from '../content/map';
import { nodeProgress, threatCap } from '../threat';
import type { TheatreNode } from '../types';
import { shuffled } from '../util';
import { assignArchetypes, categoryCap, MIN_CATEGORIES } from './assign';
import { dressNode } from './dress';
import { buildTheatre, COLUMNS, type Skeleton } from './graph';

export interface GeneratedOperation {
  codename: string;
  theatres: Biome[];
  nodes: TheatreNode[];
}

/** Mission slots per theatre: 4-6 each, trimmed so the operation stays at least 60 % native. */
function missionSlots(rng: Pcg32, theatres: number): number[] {
  const slots = Array.from({ length: theatres }, () => MAP.minMissionSlots + rng.int(MAP.slotSpread));
  for (let total = slots.reduce((a, b) => a + b, 0); total > MAP.maxMissionSlots; total--) {
    const largest = slots.indexOf(Math.max(...slots));
    slots[largest] = (slots[largest] ?? MAP.minMissionSlots) - 1;
  }
  return slots;
}

export function generateOperation(difficulty: number, hazard: number, seed: number): GeneratedOperation {
  const root = new Pcg32(seed >>> 0, 0x0904);
  const rules = hazardRules(hazard);
  const names = root.fork('codename');
  const codename = `${names.pick(codenames.adjectives)} ${names.pick(codenames.nouns)}`;
  const theatres = shuffled(root.fork('theatres'), BIOMES);

  const shapeRng = root.fork('graph');
  const slots = missionSlots(shapeRng, theatres.length);
  const skeletons: Skeleton[] = theatres.map((_, t) => {
    const third = shapeRng.chance(MAP.thirdSupportChance) ? 1 : 0;
    const supports = Math.max(0, MAP.supportNodes + third - rules.fewerSupportNodes);
    return buildTheatre(shapeRng, t, slots[t] ?? MAP.minMissionSlots, supports);
  });

  const placed = assignArchetypes(root.fork('archetypes'), theatres, skeletons);
  const dressRng = root.fork('dress');
  const ctx = { difficulty, readiness: rules.readinessStart, rules, theatres: theatres.length };
  const nodes = skeletons.flatMap((s) => s.nodes);
  for (const node of nodes) {
    const id = placed.get(node.id);
    const biome = theatres[node.theatre];
    if (id && biome) dressNode(dressRng, node, id, biome, ctx);
  }

  // A finished keystone opens the next theatre's first column.
  skeletons.forEach((skeleton, t) => {
    const keystone = nodes.find((n) => n.id === skeleton.keystoneId);
    const next = skeletons[t + 1];
    if (keystone && next) keystone.links = next.nodes.filter((n) => n.column === 0).map((n) => n.id);
  });
  return { codename, theatres, nodes };
}

/**
 * The ways a generated map can be wrong; empty when it is sound. Used by tests and as a guard.
 * Given the `difficulty` it was generated at, the threat ramp is checked too: no fresh node
 * may be rated above the cap for how far along it lies (the first column: 3 on Standard).
 */
export function validateOperation(op: GeneratedOperation, difficulty?: number): string[] {
  const problems: string[] = [];
  for (const node of op.nodes) {
    if (node.kind === 'mission' && !node.params) problems.push(`${node.id}: a mission node with no mission`);
    if (!node.params) continue;
    const { threat } = node.params;
    if (!Number.isInteger(threat) || threat < 1 || threat > 9) problems.push(`${node.id}: threat ${threat}`);
    if (difficulty === undefined) continue;
    const cap = threatCap(difficulty, nodeProgress(node.theatre, node.column, op.theatres.length));
    if (threat > cap) problems.push(`${node.id}: threat ${threat} is over the cap of ${cap} this early`);
  }
  const byId = new Map(op.nodes.map((n) => [n.id, n]));
  const flown = op.nodes.filter((n) => n.params !== null);
  const seen = new Set(flown.map((n) => n.params?.archetype));
  if (seen.size < 12) problems.push(`only ${seen.size} archetypes appear`);
  if (flown.filter((n) => !n.variant).length < 0.6 * flown.length) problems.push('under 60 % native pairings');

  op.theatres.forEach((_, t) => {
    const here = op.nodes.filter((n) => n.theatre === t);
    const kinds = here.filter((n) => n.params).map((n) => n.params?.archetype);
    if (new Set(kinds).size !== kinds.length) problems.push(`theatre ${t}: archetype repeated`);
    // The mix (10 §4): the 1987 spine among the natives, and no one category taking the theatre over.
    const missions = here.filter((n) => n.params);
    const spine = missions.filter((n) => !n.variant && n.params && isOriginal(archetype(n.params.archetype)));
    if (spine.length < 2) problems.push(`theatre ${t}: only ${spine.length} of the original twelve among its natives`);
    const categories = new Map<string, number>();
    for (const n of missions) {
      const category = n.params ? archetype(n.params.archetype).facts.category : '';
      categories.set(category, (categories.get(category) ?? 0) + 1);
    }
    if (categories.size < MIN_CATEGORIES) problems.push(`theatre ${t}: only ${categories.size} categories`);
    for (const [category, n] of categories) {
      if (n > categoryCap(missions.length)) problems.push(`theatre ${t}: ${n} ${category} missions`);
    }
    for (let c = 0; c < COLUMNS; c++) {
      const column = here.filter((n) => n.column === c);
      if (column.length < 1 || column.length > 3) problems.push(`theatre ${t} column ${c}: ${column.length} nodes`);
      const supports = column.filter((n) => n.kind !== 'mission').length;
      if (supports > 1) problems.push(`theatre ${t} column ${c}: two support nodes`);
      const orphan = c > 0 && column.some((n) => !here.some((m) => m.links.includes(n.id)));
      if (orphan) problems.push(`theatre ${t}: orphan node`);
    }
    for (const node of here.filter((n) => !n.keystone)) {
      const bad = node.links.length < 1 || node.links.length > 2
        || node.links.some((id) => byId.get(id)?.theatre !== t || byId.get(id)?.column !== node.column + 1);
      if (bad) problems.push(`${node.id}: bad links`);
    }
    // A route through the four columns with no strict-quiet mission on it.
    const loud = (n: TheatreNode): boolean => !n.params || !archetype(n.params.archetype).strictQuiet;
    let frontier = here.filter((n) => n.column === 0 && loud(n));
    for (let c = 1; c < COLUMNS; c++) {
      frontier = here.filter((n) => n.column === c && loud(n) && frontier.some((f) => f.links.includes(n.id)));
    }
    if (frontier.length === 0) problems.push(`theatre ${t}: no loud route`);
  });
  return problems;
}
